import React, { useState, useEffect } from 'react';
import { usePWA, useInstallPrompt } from '../hooks/usePWA';
import { useAuth } from '../context/AuthContext';
import { preferencesAPI } from '../services/api';
import './InstallPWAPopup.css';

const InstallPWAPopup: React.FC = () => {
  const { isPWA, platform } = usePWA();
  const { isInstallable, promptInstall } = useInstallPrompt();
  const { user, isAuthenticated } = useAuth();
  const [showPopup, setShowPopup] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const checkDismissStatus = async () => {
      // Solo mostrar si no es PWA y es instalable
      if (!isPWA && isInstallable && !isDismissed) {
        let dontShowAgain = false;
        
        // Primero verificar localStorage para carga inmediata
        const localDismissed = localStorage.getItem('pwa-install-popup-dismissed');
        if (localDismissed === 'true') {
          dontShowAgain = true;
          setIsDismissed(true);
        }
        
        // Si el usuario está autenticado, verificar también en el servidor
        if (isAuthenticated && user && !dontShowAgain) {
          try {
            const response = await preferencesAPI.get('pwa-install-popup-dismissed');
            const serverDismissed = response.data.data.value === 'true';
            
            if (serverDismissed) {
              dontShowAgain = true;
              setIsDismissed(true);
              // Sincronizar con localStorage
              localStorage.setItem('pwa-install-popup-dismissed', 'true');
            }
          } catch (error) {
            // Si hay error 404, la preferencia no existe en servidor
            // Si hay localStorage, migrar al servidor
            if ((error as any)?.response?.status === 404 && localDismissed === 'true') {
              try {
                await preferencesAPI.set('pwa-install-popup-dismissed', 'true');
              } catch (setError) {
                console.warn('No se pudo migrar preferencia PWA al servidor:', setError);
              }
            }
          }
        }
        
        if (!dontShowAgain) {
          // Mostrar popup después de 3 segundos
          const timer = setTimeout(() => {
            setShowPopup(true);
            // Pequeño delay para animación
            setTimeout(() => setIsVisible(true), 50);
          }, 3000);

          return () => clearTimeout(timer);
        }
      }
    };

    checkDismissStatus();
  }, [isPWA, isInstallable, isAuthenticated, user, isDismissed]);

  const handleInstall = async () => {
    if (platform === 'ios') {
      // Para iOS, mostrar instrucciones
      setShowIOSInstructions(true);
    } else {
      const success = await promptInstall();
      if (success) {
        closePopup();
      }
    }
  };

  const handleClose = () => {
    closePopup();
  };

  const handleDontShowAgain = async () => {
    setIsDismissed(true);
    localStorage.setItem('pwa-install-popup-dismissed', 'true');
    
    // Guardar en servidor si el usuario está autenticado
    if (isAuthenticated && user) {
      try {
        await preferencesAPI.set('pwa-install-popup-dismissed', 'true');
      } catch (error) {
        console.warn('No se pudo guardar preferencia PWA en servidor:', error);
        // No bloquear el cierre del popup si falla el servidor
      }
    }
    
    closePopup();
  };

  const closePopup = () => {
    setIsVisible(false);
    // Esperar animación antes de ocultar completamente
    setTimeout(() => setShowPopup(false), 300);
  };

  if (!showPopup) {
    return null;
  }

  return (
    <div className={`install-popup-overlay ${isVisible ? 'visible' : ''}`}>
      <div className={`install-popup ${isVisible ? 'visible' : ''}`}>
        <button className="install-popup-close" onClick={handleClose}>
          ×
        </button>
        
        <div className="install-popup-icon">
          📱
        </div>
        
        <h3 className="install-popup-title">
          ¡Instala MalDolor!
        </h3>
        
        {!showIOSInstructions ? (
          <>
            <p className="install-popup-description">
              Instala la aplicación en tu dispositivo para una mejor experiencia:
            </p>
            
            <ul className="install-popup-benefits">
              <li>✅ Acceso más rápido</li>
              <li>✅ Funciona sin conexión</li>
              <li>✅ Notificaciones</li>
              <li>✅ Como una app nativa</li>
            </ul>
            
            <div className="install-popup-buttons">
              <button 
                className="btn-install-primary"
                onClick={handleInstall}
              >
                📱 {platform === 'ios' ? 'Ver Instrucciones' : 'Instalar Ahora'}
              </button>
              
              <button 
                className="btn-install-secondary"
                onClick={handleClose}
              >
                Más tarde
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="install-popup-description">
              Para instalar en iOS Safari:
            </p>
            
            <ol className="ios-instructions">
              <li>
                <span>1️⃣</span> Toca el botón <strong>Compartir</strong> 
                <div className="ios-icon">📤</div>
              </li>
              <li>
                <span>2️⃣</span> Selecciona <strong>"Añadir a pantalla de inicio"</strong>
                <div className="ios-icon">➕📱</div>
              </li>
              <li>
                <span>3️⃣</span> Toca <strong>"Añadir"</strong> para confirmar
                <div className="ios-icon">✅</div>
              </li>
            </ol>
            
            <div className="install-popup-buttons">
              <button 
                className="btn-install-primary"
                onClick={() => setShowIOSInstructions(false)}
              >
                ← Volver
              </button>
              
              <button 
                className="btn-install-secondary"
                onClick={handleClose}
              >
                Entendido
              </button>
            </div>
          </>
        )}
        
        <button 
          className="btn-install-dont-show"
          onClick={handleDontShowAgain}
        >
          No volver a mostrar
        </button>
      </div>
    </div>
  );
};

export default InstallPWAPopup;